import { Link, useLocation, useNavigate } from "react-router-dom";
import { Shield, Mail, Phone, MapPin } from "lucide-react";

const FOOTER_SECTIONS = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A0F1A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-7 h-7 text-[#2563EB]" />
              <span className="font-heading text-xl font-extrabold tracking-tight">
                Trademark<span className="text-[#2563EB]"> Officers</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Your trusted partner for comprehensive trademark registration services in the United States. We protect brands with precision and dedication.
            </p>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#1E3A8A]/20 text-[#2563EB] text-xs font-medium border border-[#1E3A8A]/30">
                USPTO Filing Experts
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-300 mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {FOOTER_SECTIONS.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => handleNavClick(link.id)}
                    data-testid={`footer-link-${link.id}`}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-300 mb-5">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid="footer-link-privacy"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-conditions"
                  data-testid="footer-link-terms"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-300 mb-5">
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <a
                  href="mailto:info@trademarkofficers.us"
                  data-testid="footer-email"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  info@trademarkofficers.us
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <a
                  href="tel:+11231234567"
                  data-testid="footer-phone"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  +1 (123) 123-4567
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <span className="text-slate-400 text-sm">
                  11040 Santa Monica Blvd #310,
                  <br />
                  Los Angeles, CA 90025
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-slate-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-xs" data-testid="footer-copyright">
              &copy; 2026 Trademark Officers. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-conditions"
                className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
